import React, { useState, useCallback } from 'react';
import ipc from '../services/ipc';
import { runTemplateLCA } from '../services/lcaCalculator';
import { runTemplateTEA } from '../services/teaCalculator';
import { resolveAllSubInventories } from '../services/subInventoryResolver';
import { TEABarChart, LCABarChart, ScenarioCompareChart } from './ResultsCharts';

/**
 * ComparePanel — Top-level tab for side-by-side template comparison.
 *
 * Lets user load a second template, runs TEA/LCA on both the current project
 * and the comparison template, and shows delta tables + charts.
 *
 * Props:
 *   project          – current project
 *   analysisResults  – current project's { lca, tea }
 *   onRunAll         – callback to run analysis on current project
 *   onSaveComparison – callback to persist comparison results to project
 */

// Flat list of all templates from the catalog
const ALL_TEMPLATES = [
  { id: 'nmc111', name: 'NMC111 Cell Manufacturing', template: 'nmc111_cell_manufacturing.json', chemistry: 'NMC111' },
  { id: 'nmc622', name: 'NMC622 Cell Manufacturing', template: 'nmc622_cell_manufacturing.json', chemistry: 'NMC622' },
  { id: 'nmc811', name: 'NMC811 Cell Manufacturing', template: 'nmc811_cell_manufacturing.json', chemistry: 'NMC811' },
  { id: 'lfp', name: 'LFP Cell Manufacturing', template: 'lfp_cell_manufacturing.json', chemistry: 'LFP' },
  { id: 'braws', name: 'BRAWS Recycling', template: 'braws_template.json', chemistry: 'Li-NMC' },
];

const formatNumber = (n) => {
  if (n === undefined || n === null || isNaN(n)) return '—';
  if (Math.abs(n) < 0.001 && n !== 0) return n.toExponential(2);
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return Number(n.toFixed(4)).toString();
};

const pctChange = (a, b) => {
  if (!a || a === 0) return b ? '+∞' : '0%';
  const pct = ((b - a) / Math.abs(a)) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

const ComparePanel = ({ project, analysisResults, onRunAll, onSaveComparison }) => {
  const [compareTemplate, setCompareTemplate] = useState(null);
  const [compareResults, setCompareResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const currentName = project?.project_info?.name || 'Current Project';
  const hasCurrentResults = analysisResults?.lca || analysisResults?.tea;

  const handleLoadTemplate = useCallback(async () => {
    const tmpl = ALL_TEMPLATES.find(t => t.id === selectedTemplateId);
    if (!tmpl) return;

    setLoading(true);
    setError(null);
    try {
      const templateData = await ipc.invoke('load-template', tmpl.template);
      if (!templateData?.processes) throw new Error('Template has no processes');

      setCompareTemplate({ ...templateData, _templateInfo: tmpl });

      // Run analysis on comparison template
      const resolvedSubs = await resolveAllSubInventories(templateData);
      const lca = runTemplateLCA(templateData, resolvedSubs);
      const tea = runTemplateTEA(templateData, resolvedSubs);
      setCompareResults({ lca, tea });

      // If current project doesn't have results yet, trigger analysis
      if (!hasCurrentResults && onRunAll) {
        onRunAll();
      }
    } catch (err) {
      setError(err.message);
      setCompareTemplate(null);
      setCompareResults(null);
    } finally {
      setLoading(false);
    }
  }, [selectedTemplateId, hasCurrentResults, onRunAll]);

  const handleSave = useCallback(() => {
    if (!onSaveComparison || !compareTemplate || !compareResults) return;
    onSaveComparison({
      comparison_template: compareTemplate._templateInfo?.name || 'Unknown',
      comparison_template_id: compareTemplate._templateInfo?.id,
      comparison_results: compareResults,
      compared_at: new Date().toISOString(),
    });
  }, [onSaveComparison, compareTemplate, compareResults]);

  const handleClear = () => {
    setCompareTemplate(null);
    setCompareResults(null);
    setSelectedTemplateId('');
    setError(null);
  };

  // Build comparison data
  const currentTEA = analysisResults?.tea;
  const compareTEA = compareResults?.tea;
  const currentLCA = analysisResults?.lca;
  const compareLCA = compareResults?.lca;
  const compareName = compareTemplate?._templateInfo?.name || 'Comparison';

  return (
    <div style={{ padding: '12px', overflow: 'auto', flex: 1 }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '12px' }}>
        Scenario Comparison
      </div>

      {/* Template picker */}
      {!compareTemplate && (
        <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '16px', border: '1px solid #e8eaed' }}>
          <div style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '8px' }}>
            Load a template to compare with <strong>{currentName}</strong>:
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={selectedTemplateId}
              onChange={e => setSelectedTemplateId(e.target.value)}
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '13px' }}
            >
              <option value="">Select a template...</option>
              {ALL_TEMPLATES.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.chemistry})
                </option>
              ))}
            </select>
            <button
              onClick={handleLoadTemplate}
              disabled={!selectedTemplateId || loading}
              style={{
                padding: '6px 16px', background: '#1a73e8', color: '#fff',
                border: 'none', borderRadius: 4, fontSize: '13px', cursor: 'pointer',
                fontWeight: 500, opacity: selectedTemplateId ? 1 : 0.5,
              }}
            >
              {loading ? 'Loading...' : 'Load & Compare'}
            </button>
          </div>
          {error && (
            <div style={{ color: '#d93025', fontSize: '12px', marginTop: '6px' }}>Error: {error}</div>
          )}
          {!hasCurrentResults && (
            <div style={{ color: '#e65100', fontSize: '12px', marginTop: '8px', fontStyle: 'italic' }}>
              Run analysis on the current project first (Analysis tab), or it will be run automatically when you load a comparison.
            </div>
          )}
        </div>
      )}

      {/* Comparison loaded — show header + clear */}
      {compareTemplate && (
        <>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: '#e8f0fe', borderRadius: 6, padding: '10px 14px', marginBottom: '12px',
          }}>
            <div>
              <span style={{ fontSize: '12px', color: '#666' }}>Comparing:</span>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '2px' }}>
                <span style={{ fontWeight: 600, color: '#1a73e8', fontSize: '13px' }}>{currentName}</span>
                <span style={{ color: '#999' }}>vs</span>
                <span style={{ fontWeight: 600, color: '#e65100', fontSize: '13px' }}>{compareName}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={handleSave}
                style={{ padding: '4px 12px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 4, fontSize: '11px', cursor: 'pointer', fontWeight: 500 }}>
                Save Comparison
              </button>
              <button onClick={handleClear}
                style={{ padding: '4px 12px', background: '#f5f5f5', color: '#333', border: '1px solid #ccc', borderRadius: 4, fontSize: '11px', cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          </div>

          {/* TEA Comparison */}
          {(currentTEA || compareTEA) && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>
                Techno-Economic Analysis
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Metric</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', color: '#1a73e8' }}>{currentName}</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', color: '#e65100' }}>{compareName}</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>
                    <td style={{ padding: '6px 8px' }}>Total Cost (USD)</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {currentTEA ? `$${formatNumber(currentTEA.total_cost)}` : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {compareTEA ? `$${formatNumber(compareTEA.total_cost)}` : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: (compareTEA?.total_cost || 0) > (currentTEA?.total_cost || 0) ? '#d93025' : '#2e7d32' }}>
                      {currentTEA && compareTEA ? pctChange(currentTEA.total_cost, compareTEA.total_cost) : '—'}
                    </td>
                  </tr>
                  {/* Per-process breakdown */}
                  {currentTEA?.process_breakdown && Object.entries(currentTEA.process_breakdown).map(([procId, data]) => {
                    const compProc = compareTEA?.process_breakdown?.[procId];
                    return (
                      <tr key={procId} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '4px 8px 4px 20px', color: '#666' }}>{data.process_name?.replace(/_/g, ' ')}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' }}>
                          ${formatNumber(data.total_cost)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' }}>
                          {compProc ? `$${formatNumber(compProc.total_cost)}` : '—'}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px', color: '#888' }}>
                          {compProc ? pctChange(data.total_cost, compProc.total_cost) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* LCA Comparison */}
          {(currentLCA || compareLCA) && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>
                Life Cycle Assessment (TRACI v2.1)
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Impact Category</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', color: '#1a73e8' }}>{currentName}</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', color: '#e65100' }}>{compareName}</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLCA?.results && Object.entries(currentLCA.results).map(([key, data]) => {
                    const compData = compareLCA?.results?.[key];
                    return (
                      <tr key={key} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '4px 8px', fontSize: '11px' }}>
                          {key.replace('TRACI v2.1 - ', '')}
                          <span style={{ color: '#999', marginLeft: 4, fontSize: '10px' }}>({data.unit})</span>
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' }}>
                          {formatNumber(data.total)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' }}>
                          {compData ? formatNumber(compData.total) : '—'}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px',
                          color: compData && compData.total > data.total ? '#d93025' : '#2e7d32' }}>
                          {compData ? pctChange(data.total, compData.total) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Charts */}
          {currentTEA && compareTEA && (
            <div style={{ marginBottom: '16px' }}>
              <ScenarioCompareChart
                scenarioA={{ name: currentName, tea: currentTEA, lca: currentLCA }}
                scenarioB={{ name: compareName, tea: compareTEA, lca: compareLCA }}
              />
            </div>
          )}

          {/* No results message */}
          {!hasCurrentResults && !compareResults && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              Run analysis on both projects to see comparison results.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ComparePanel;
